// vistas/VistaResumen.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Edit3, ShoppingCart, Save, Loader2, Trash2, RotateCcw, Percent, Tag, Receipt, FileText, Minus, Plus, User, FileDown, MessageSquare, TrendingDown, AlertTriangle, Search, X, Paperclip, FileUp, Loader } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dimassiLogo from '../assets/Dimassi_Logo_Negro.png';

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);

// ✅ FUNCIÓN PARA COMPRIMIR IMAGEN A MÁXIMO 1MB
const compressImageToMax1MB = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Reducir dimensiones si es muy grande
        const maxDimension = 1600;
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Intentar comprimir con calidad decreciente
        let quality = 0.8;
        let compressed = canvas.toDataURL('image/jpeg', quality);
        
        // Reducir calidad hasta que sea menor a 1MB
        while (compressed.length > 1 * 1024 * 1024 && quality > 0.05) {
          quality -= 0.05;
          compressed = canvas.toDataURL('image/jpeg', quality);
        }
        
        // Si aún es muy grande, reducir más dimensiones
        if (compressed.length > 1 * 1024 * 1024) {
          width = Math.round(width * 0.7);
          height = Math.round(height * 0.7);
          canvas.width = width;
          canvas.height = height;
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          compressed = canvas.toDataURL('image/jpeg', 0.5);
        }
        
        // Convertir base64 a Blob
        const byteString = atob(compressed.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: 'image/jpeg' });
        
        resolve({
          blob: blob,
          name: file.name.replace(/\.[^.]+$/, '.jpg'),
          type: 'image/jpeg',
          size: blob.size,
          originalSize: file.size
        });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const VistaResumen = ({ hook }) => {
  const {
    selectedClients, orderLines, productClientAssignment,
    clientOCs, setClientOCs, clientDescuentos,
    clientesOptions, savingOrder,
    handleUpdateClientQuantity, handleUpdateClientDiscount,
    handleUpdateClientPacDiscount, handleRemoveProduct,
    handleSaveOrder, limpiarFormulario, getClientPrecios,
    comentario, setComentario,
  } = hook;

  const clienteId = selectedClients[0];
  
  // Estado para el buscador
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para el archivo de OC
  const [ocFile, setOcFile] = useState(null);
  const [ocFileName, setOcFileName] = useState('');
  const [ocFileSize, setOcFileSize] = useState('');
  const [ocFileOriginalSize, setOcFileOriginalSize] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);

   useEffect(() => {
    if (orderLines.length === 0 && !savingOrder) {
      // Limpiar estados del archivo OC
      setOcFile(null);
      setOcFileName('');
      setOcFileSize('');
      setOcFileOriginalSize('');
      setIsCompressing(false);
      
      // Limpiar el input file
      const fileInput = document.getElementById('oc-file-input');
      if (fileInput) fileInput.value = '';
    }
  }, [orderLines.length, savingOrder]);

  // ✅ También puedes agregar otro useEffect para limpiar cuando cambia el cliente
  useEffect(() => {
    // Limpiar archivo OC cuando cambia el cliente
    setOcFile(null);
    setOcFileName('');
    setOcFileSize('');
    setOcFileOriginalSize('');
    setIsCompressing(false);
    
    const fileInput = document.getElementById('oc-file-input');
    if (fileInput) fileInput.value = '';
  }, [clienteId]);
  
  // CORRECCIÓN: Normalizar el ID para la búsqueda
  const clienteIdNormalizado = clienteId ? String(clienteId).padStart(4, '0') : null;
  
  const clienteInfo = clientesOptions.find(c => {
    if (!clienteIdNormalizado) return false;
    const cValue = String(c.value).padStart(4, '0');
    return cValue === clienteIdNormalizado;
  });

  // Verificar si el cliente es exento
  const isExento = clienteInfo?.isExento || clienteInfo?.exento || clienteInfo?.is_exento || false;

  // ✅ Función para manejar la selección del archivo OC con compresión
  const handleOCFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tipo de archivo
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert('Formato no permitido. Use PDF, JPG, PNG o WebP.');
      return;
    }
    
    // Validar tamaño original máximo (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo original es demasiado grande. Máximo 10MB.');
      return;
    }
    
    setIsCompressing(true);
    
    try {
      let finalFile = file;
      let finalName = file.name;
      let finalSize = file.size;
      let originalSize = file.size;
      
      // Si es imagen, comprimir
      if (file.type.startsWith('image/')) {
        const compressed = await compressImageToMax1MB(file);
        finalFile = compressed.blob;
        finalName = compressed.name;
        finalSize = compressed.size;
        originalSize = compressed.originalSize;
      } 
      // Si es PDF y es mayor a 1MB, intentar comprimir (limitado)
      else if (file.type === 'application/pdf' && file.size > 1 * 1024 * 1024) {
        // Para PDFs, no podemos comprimir fácilmente en frontend
        // Usamos el original pero advertimos
        console.warn('PDF grande no se puede comprimir en frontend');
        finalFile = file;
        finalName = file.name;
        finalSize = file.size;
        originalSize = file.size;
      }
      // Si es PDF menor a 1MB, usar directamente
      else {
        finalFile = file;
        finalName = file.name;
        finalSize = file.size;
        originalSize = file.size;
      }
      
      setOcFile(finalFile);
      setOcFileName(finalName);
      setOcFileSize((finalSize / 1024).toFixed(2) + ' KB');
      
      if (originalSize !== finalSize) {
        setOcFileOriginalSize((originalSize / 1024).toFixed(2) + ' KB');
      } else {
        setOcFileOriginalSize('');
      }
      
    } catch (error) {
      console.error('Error comprimiendo archivo:', error);
      alert('Error al comprimir el archivo. Intente con otro archivo.');
    } finally {
      setIsCompressing(false);
    }
  };
  
  // Función para eliminar el archivo OC
  const handleRemoveOCFile = () => {
    setOcFile(null);
    setOcFileName('');
    setOcFileSize('');
    setOcFileOriginalSize('');
    const fileInput = document.getElementById('oc-file-input');
    if (fileInput) fileInput.value = '';
  };

  // Función para verificar si un producto está activo
  const isProductActive = (line) => {
    if (line.activo !== undefined && line.activo !== null) {
      return line.activo === true || line.activo === 1 || line.activo === '1' || line.activo === 'true';
    }
    if (line.is_active !== undefined && line.is_active !== null) {
      return line.is_active === true || line.is_active === 1 || line.is_active === '1' || line.is_active === 'true';
    }
    if (line.estado !== undefined && line.estado !== null) {
      return line.estado === 'activo' || line.estado === 'ACTIVO' || line.estado === 'Activo' || line.estado === 1 || line.estado === '1';
    }
    return true;
  };

  // Filtrar productos según búsqueda
  const filteredOrderLines = useMemo(() => {
    if (!searchTerm.trim()) return orderLines;
    
    const term = searchTerm.trim().toLowerCase();
    
    return orderLines.filter(line => {
      const sku = String(line.sku || '').toLowerCase();
      const nombre = String(line.nombre_producto || '').toLowerCase();
      const marca = String(line.marca || '').toLowerCase();
      const ref = String(line.ref || '').toLowerCase();
      
      return sku.includes(term) || 
             nombre.includes(term) || 
             marca.includes(term) || 
             ref.includes(term);
    });
  }, [orderLines, searchTerm]);

  // Función para descargar proforma PDF
  const downloadProformaPDF = () => {
    if (!clienteId || orderLines.length === 0) return;
    
    const today = new Date();
    const formattedDate = today.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const formattedTime = today.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 10;

    if (dimassiLogo) {
      doc.addImage(dimassiLogo, 'SVG', 14, currentY, 45, 0);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DIMASSI, C.A.', pageWidth - 70, currentY + 2, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.2);
    doc.text('Domicilio Fiscal: Av. Principal de La Castellana, Calle Blandin, Edif. Torre Digitel, Piso 12. Of. B.', pageWidth - 70, currentY + 6, { align: 'center' });
    doc.text('Caracas (Chacao) Miranda. Zona Postal 1060', pageWidth - 70, currentY + 9, { align: 'center' });
    doc.text('www.dimassicorp.com     @dimassivzla', pageWidth - 70, currentY + 12, { align: 'center' });

    currentY += 22;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('PROFORMA DE PEDIDO', 14, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(`Fecha: ${formattedDate}`, pageWidth - 14, currentY, { align: 'right' });
    
    currentY += 6;
    
    doc.text(`Cliente: ${clienteId} - ${clienteInfo?.nombre || 'Cliente'}`, 14, currentY);
    doc.text(`Hora: ${formattedTime}`, pageWidth - 14, currentY, { align: 'right' });

    currentY += 6;

    doc.text(`Condición: ${isExento ? 'EXENTO' : 'CONTRIBUYENTE'}`, 14, currentY);
    
    if (clienteInfo?.porc_desc > 0) {
      currentY += 5;
      doc.text(`Descuento Cliente: ${clienteInfo.porc_desc}%`, 14, currentY);
    }

    currentY += 10;

    if (comentario && comentario.trim() !== '') {
      doc.setFontSize(7);
      doc.text(`Observación: ${comentario}`, 14, currentY);
      currentY += 5;
    }

    currentY += 5;

    const rows = [];
    let totalBultos = 0;
    let totalBruto = 0;
    let totalDescPropuesto = 0;
    let totalPAC = 0;

    const formatearMoneda = (valor) => {
      return valor.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    orderLines.forEach((line, idx) => {
      const assign = productClientAssignment[line.sku]?.[clienteId];
      const precios = getClientPrecios(line.sku, clienteId);
      
      if (!isProductActive(line) || !assign || assign.cantidad <= 0 || !precios || precios.precio_usd <= 0) return;
      
      const cantidad = assign.cantidad;
      const precioUSD = precios.precio_usd || 0;
      const descManual = assign.descuento || 0;
      const descPAC = assign.descuento_pac || 0;
      const subtotalBruto = cantidad * precioUSD;
      const montoDesc = subtotalBruto * (descManual / 100);
      const montoPAC = subtotalBruto * (descPAC / 100);
      const subtotalFinal = subtotalBruto - montoDesc - montoPAC;
      
      totalBultos += cantidad;
      totalBruto += subtotalBruto;
      totalDescPropuesto += montoDesc;
      totalPAC += montoPAC;
      
      rows.push([
        idx + 1,
        line.sku,
        line.nombre_producto,
        `${cantidad} ${line.uni_venta || 'UND'}`,
        `$ ${formatearMoneda(precioUSD)}`,
        descManual > 0 ? `${descManual}%` : '-',
        descPAC > 0 ? `${descPAC}%` : '-',
        `$ ${formatearMoneda(subtotalFinal)}`
      ]);
    });

    autoTable(doc, {
      startY: currentY,
      head: [['#', 'SKU', 'Producto', 'Cantidad', 'Precio USD', 'Desc. Prop.', 'PAC', 'Subtotal']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: [31, 41, 55],
        textColor: [255, 255, 255],
        fontSize: 7,
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 25 },
        3: { cellWidth: 25, halign: 'center' },
        4: { halign: 'right' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
        7: { halign: 'right' }
      },
      styles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      margin: { left: 14, right: 14 }
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    const derechaX = pageWidth - 14;
    
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 90, finalY, derechaX, finalY);
    
    let totalY = finalY + 6;
    doc.setFontSize(9.5);
    
    doc.setFont('helvetica', 'normal');
    doc.text('Total Bultos:', derechaX - 35, totalY, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(`${totalBultos}`, derechaX, totalY, { align: 'right' });
    
    totalY += 5.5;
    doc.setFont('helvetica', 'normal');
    doc.text('Total sin descuento:', derechaX - 35, totalY, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(`$ ${formatearMoneda(totalBruto)}`, derechaX, totalY, { align: 'right' });

    if (totalDescPropuesto > 0) {
      totalY += 5.5;
      doc.setFont('helvetica', 'normal');
      doc.text('Total Desc. Propuesto:', derechaX - 35, totalY, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`-$ ${formatearMoneda(totalDescPropuesto)}`, derechaX, totalY, { align: 'right' });
    }

    if (totalPAC > 0) {
      totalY += 5.5;
      doc.setFont('helvetica', 'normal');
      doc.text('Total PAC:', derechaX - 35, totalY, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`-$ ${formatearMoneda(totalPAC)}`, derechaX, totalY, { align: 'right' });
    }

    const subtotalDespuesDescuentos = totalBruto - totalDescPropuesto - totalPAC;
    
    if (totalDescPropuesto > 0 || totalPAC > 0) {
      totalY += 5.5;
      doc.setFont('helvetica', 'bold');
      doc.text('Subtotal:', derechaX - 35, totalY, { align: 'right' });
      doc.text(`$ ${formatearMoneda(subtotalDespuesDescuentos)}`, derechaX, totalY, { align: 'right' });
    }

    const descCliente = clientDescuentos[clienteId] || 0;
    if (descCliente > 0) {
      totalY += 5.5;
      doc.setFont('helvetica', 'normal');
      doc.text(`Desc. Cliente (${descCliente}%):`, derechaX - 35, totalY, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`-$ ${formatearMoneda(subtotalDespuesDescuentos * descCliente / 100)}`, derechaX, totalY, { align: 'right' });
      
      totalY += 5.5;
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL CON DESCUENTO:', derechaX - 35, totalY, { align: 'right' });
      doc.text(`$ ${formatearMoneda(subtotalDespuesDescuentos * (1 - descCliente / 100))}`, derechaX, totalY, { align: 'right' });
    } else {
      totalY += 5.5;
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL FINAL:', derechaX - 35, totalY, { align: 'right' });
      doc.text(`$ ${formatearMoneda(subtotalDespuesDescuentos)}`, derechaX, totalY, { align: 'right' });
    }

    doc.save(`Proforma_${clienteId}.pdf`);
  };

  return (
    <div className="mass-col mass-col-summary">
      <div className="col-header">
        <Edit3 size={18} />
        <h3>Resumen del Pedido</h3>
        
        {orderLines.length > 0 && (
          <button
            onClick={downloadProformaPDF}
            style={{
              marginLeft: 'auto',
              background: 'rgba(59,130,246,0.2)',
              border: '1px solid rgba(59,130,246,0.4)',
              color: '#3b82f6',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.65rem',
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif'
            }}
            title="Descargar Proforma PDF"
          >
            <FileDown size={14} />
            Proforma
          </button>
        )}
      </div>

      {orderLines.length === 0 ? (
        <div className="empty-state">
          <ShoppingCart size={40} />
          <p>Agregue productos</p>
        </div>
      ) : (
        <>
          {/* HEADER DEL CLIENTE */}
          <div style={{
            padding: '10px 12px',
            background: 'rgba(0,186,82,0.08)',
            borderBottom: '2px solid rgba(0,186,82,0.25)',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <User size={16} style={{ color: '#00ba52' }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#00ba52', fontFamily: 'monospace' }}>
                {clienteId}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#fff', fontWeight: 600 }}>
                {clienteInfo?.nombre || 'Cliente'}
              </span>
              {isExento ? (
                <span style={{ fontSize: '0.5rem', fontWeight: 700, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', padding: '2px 6px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Tag size={9} /> EXENTO
                </span>
              ) : (
                <span style={{ fontSize: '0.5rem', fontWeight: 600, background: 'rgba(59,130,246,0.15)', color: '#3b82f6', padding: '2px 6px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  <Receipt size={9} /> CONTRIB
                </span>
              )}
              {clienteInfo?.porc_desc > 0 && (
                <span style={{ fontSize: '0.55rem', color: '#f87171', fontWeight: 600 }}>
                  Desc. Cliente: {clienteInfo.porc_desc}%
                </span>
              )}
              <span style={{ fontSize: '0.55rem', color: '#4ade80', fontWeight: 700, marginLeft: 'auto', background: 'rgba(0,186,82,0.15)', padding: '2px 8px', borderRadius: '10px' }}>
                USD
              </span>
            </div>
          </div>

          {/* BUSCADOR DE PRODUCTOS */}
          <div style={{
            padding: '6px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '6px',
              padding: '4px 8px'
            }}>
              <Search size={12} style={{ color: '#666', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Buscar por SKU, nombre, marca o ref..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '0.65rem',
                  outline: 'none',
                  width: '100%',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#666',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            
            {searchTerm && (
              <div style={{
                fontSize: '0.5rem',
                color: '#666',
                marginTop: '3px',
                paddingLeft: '4px'
              }}>
                {filteredOrderLines.length} de {orderLines.length} productos
              </div>
            )}
          </div>

          <div className="summary-scrollable" style={{ padding: '0.5rem', flex: 1, overflowY: 'auto' }}>
            {/* LISTA DE PRODUCTOS FILTRADOS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {filteredOrderLines.map((line, idx) => {
                const assign = productClientAssignment[line.sku]?.[clienteId];
                const precios = getClientPrecios(line.sku, clienteId);
                
                const productoActivo = isProductActive(line);
                const tienePrecio = precios && (precios.precio_bs > 0 || precios.precio_usd > 0);
                const noSeVende = !productoActivo || !tienePrecio;
                
                const descManual = assign?.descuento || 0;
                const descPAC = assign?.descuento_pac || 0;
                const precioUSD = precios?.precio_usd || 0;
                const cantidad = assign?.cantidad || 0;
                const subtotalBruto = cantidad * precioUSD;
                const montoDesc = subtotalBruto * (descManual / 100);
                const montoPAC = subtotalBruto * (descPAC / 100);
                const subtotalFinal = subtotalBruto - montoDesc - montoPAC;
                const sinStock = line.stock_bl !== null && line.stock_bl !== undefined && line.stock_bl <= 0;
                
                return (
                  <div key={line.sku} style={{
                    border: noSeVende ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: noSeVende ? 'rgba(239,68,68,0.05)' : 'rgba(0,0,0,0.2)'
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '6px 10px',
                      background: 'rgba(255,255,255,0.03)',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ fontSize: '0.55rem', color: '#666', fontWeight: 700, minWidth: '18px' }}>
                        {idx + 1}.
                      </span>
                      <span style={{ fontFamily: 'monospace', color: noSeVende ? '#f87171' : '#00ba52', fontSize: '0.65rem', fontWeight: 700 }}>
                        {line.sku}
                      </span>
                      <span style={{ fontSize: '0.55rem', color: '#fff', background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                        {line.marca}
                      </span>
                      <span style={{ 
                        fontSize: '0.62rem', 
                        color: '#aaa', 
                        flex: 1, 
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        minWidth: '80px',
                        maxWidth: '100%'
                      }}>
                        {line.nombre_producto}
                      </span>
                      
                      {noSeVende && (
                        <span style={{ 
                          fontSize: '0.5rem', 
                          color: '#f87171', 
                          background: 'rgba(239,68,68,0.15)', 
                          padding: '2px 4px', 
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}>
                          <AlertTriangle size={8} />
                          {!productoActivo ? 'Inactivo' : 'Sin precio'}
                        </span>
                      )}
                      
                      <button
                        onClick={() => handleRemoveProduct(line.sku)}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '2px', flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', opacity: noSeVende ? 0.5 : 1 }}>
                      <span style={{ fontSize: '0.65rem', color: sinStock ? '#f87171' : '#2dd4bf', minWidth: '50px', textAlign: 'center', fontWeight: 600 }}>
                        {line.stock_bl !== null && line.stock_bl !== undefined ? `Stock: ${line.stock_bl}` : 'N/D'}
                      </span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '1px 3px' }}>
                        <button
                          onClick={() => handleUpdateClientQuantity(line.sku, clienteId, (cantidad) - 1)}
                          disabled={noSeVende || cantidad <= 0}
                          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 3px', opacity: cantidad <= 0 ? 0.3 : 1 }}
                        >
                          <Minus size={10} />
                        </button>
                        <input
                          type="number"
                          min="0"
                          disabled={noSeVende}
                          value={cantidad}
                          onChange={(e) => handleUpdateClientQuantity(line.sku, clienteId, e.target.value)}
                          style={{ width: '35px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.65rem', textAlign: 'center', outline: 'none' }}
                        />
                        <button
                          onClick={() => handleUpdateClientQuantity(line.sku, clienteId, cantidad + 1)}
                          disabled={noSeVende}
                          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0 3px', opacity: noSeVende ? 0.3 : 1 }}
                        >
                          <Plus size={10} />
                        </button>
                      </div>

                      <span style={{ fontSize: '0.5rem', color: '#666' }}>{line.uni_venta}</span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(59,130,246,0.08)', borderRadius: '4px', padding: '1px 4px' }}>
                        <TrendingDown size={8} style={{ color: '#60a5fa' }} />
                        <span style={{ fontSize: '0.55rem', color: '#60a5fa' }}>Desc:</span>
                        <input
                          type="number" min="0" max="100" step="1"
                          disabled={noSeVende}
                          value={descManual}
                          onChange={(e) => handleUpdateClientDiscount(line.sku, clienteId, e.target.value)}
                          style={{ width: '28px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.55rem', textAlign: 'center', outline: 'none' }}
                        />
                        <span style={{ fontSize: '0.45rem', color: '#60a5fa' }}>%</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'rgba(167,139,250,0.08)', borderRadius: '4px', padding: '1px 4px' }}>
                        <Percent size={8} style={{ color: '#a78bfa' }} />
                        <span style={{ fontSize: '0.55rem', color: '#a78bfa' }}>PAC:</span>
                        <input
                          type="number" min="0" max="100" step="1"
                          disabled={noSeVende}
                          value={descPAC}
                          onChange={(e) => handleUpdateClientPacDiscount(line.sku, clienteId, e.target.value)}
                          style={{ width: '28px', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.55rem', textAlign: 'center', outline: 'none' }}
                        />
                        <span style={{ fontSize: '0.45rem', color: '#a78bfa' }}>%</span>
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
                        <span style={{ fontSize: '0.5rem', color: '#888' }}>
                          {noSeVende ? 'No disponible' : `${formatUSD(precioUSD)} c/u`}
                        </span>
                        {!noSeVende && subtotalFinal !== subtotalBruto ? (
                          <>
                            <span style={{ fontSize: '0.45rem', color: '#666', textDecoration: 'line-through' }}>
                              {formatUSD(subtotalBruto)}
                            </span>
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff' }}>
                              {formatUSD(subtotalFinal)}
                            </span>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: noSeVende ? '#f87171' : '#fff' }}>
                            {noSeVende ? 'No procesable' : formatUSD(subtotalFinal)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {searchTerm && filteredOrderLines.length === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '0.7rem'
                }}>
                  No se encontraron productos para "{searchTerm}"
                </div>
              )}
            </div>

            {/* TOTAL Y OC */}
            <div style={{
              marginTop: '8px',
              border: '1px solid rgba(0,186,82,0.2)',
              borderRadius: '8px',
              padding: '8px 10px',
              background: 'rgba(0,186,82,0.03)'
            }}>
              {(() => {
                const descuentoCliente = clientDescuentos[clienteId] || clienteInfo?.porc_desc || 0;
                let totalSinDescuento = 0;
                let totalDescProductos = 0;
                let totalDescManual = 0;
                let totalDescPAC = 0;
                
                orderLines.forEach((line) => {
                  const assign = productClientAssignment[line.sku]?.[clienteId];
                  const precios = getClientPrecios(line.sku, clienteId);
                  if (!assign || assign.cantidad <= 0 || !precios || precios.precio_usd <= 0) return;
                  
                  const cantidad = assign.cantidad;
                  const precioUSD = precios.precio_usd || 0;
                  const descManual = assign.descuento || 0;
                  const descPAC = assign.descuento_pac || 0;
                  const subtotalBruto = cantidad * precioUSD;
                  const montoDescManual = subtotalBruto * (descManual / 100);
                  const montoDescPAC = subtotalBruto * (descPAC / 100);
                  
                  totalSinDescuento += subtotalBruto;
                  totalDescManual += montoDescManual;
                  totalDescPAC += montoDescPAC;
                  totalDescProductos += (montoDescManual + montoDescPAC);
                });
                
                const subtotalDespuesProductos = totalSinDescuento - totalDescProductos;
                const montoDescCliente = subtotalDespuesProductos * (descuentoCliente / 100);
                const totalFinal = subtotalDespuesProductos - montoDescCliente;
                
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.7rem', color: '#aaa', fontWeight: 600 }}>Total sin descuento:</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                        {formatUSD(totalSinDescuento)}
                      </span>
                    </div>

                    {totalDescManual > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                        <span style={{ fontSize: '0.6rem', color: '#60a5fa' }}>Desc. Productos:</span>
                        <span style={{ fontSize: '0.65rem', color: '#60a5fa', fontWeight: 600 }}>
                          -{formatUSD(totalDescManual)}
                        </span>
                      </div>
                    )}

                    {totalDescPAC > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                        <span style={{ fontSize: '0.6rem', color: '#a78bfa' }}>Desc. PAC:</span>
                        <span style={{ fontSize: '0.65rem', color: '#a78bfa', fontWeight: 600 }}>
                          -{formatUSD(totalDescPAC)}
                        </span>
                      </div>
                    )}

                    {totalDescProductos > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ fontSize: '0.65rem', color: '#ccc', fontWeight: 600 }}>Subtotal:</span>
                        <span style={{ fontSize: '0.7rem', color: '#ccc', fontWeight: 700 }}>
                          {formatUSD(subtotalDespuesProductos)}
                        </span>
                      </div>
                    )}

                    {descuentoCliente > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                        <span style={{ fontSize: '0.6rem', color: '#f87171' }}>
                          Desc. Cliente ({descuentoCliente}%):
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#f87171', fontWeight: 600 }}>
                          -{formatUSD(montoDescCliente)}
                        </span>
                      </div>
                    )}

                    <div style={{ 
                      display: 'flex', justifyContent: 'space-between', 
                      marginTop: '6px', padding: '6px 8px',
                      borderTop: '2px solid rgba(74,222,128,0.3)',
                      background: 'rgba(74,222,128,0.1)',
                      borderRadius: '4px'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: '#4ade80', fontWeight: 700 }}>
                        {descuentoCliente > 0 || totalDescProductos > 0 ? 'Total con descuentos:' : 'Total final:'}
                      </span>
                      <span style={{ fontSize: '0.9rem', color: '#4ade80', fontWeight: 800 }}>
                        {formatUSD(totalFinal)}
                      </span>
                    </div>
                  </>
                );
              })()}

{/* OC - CAMPO DE TEXTO LIBRE */}
<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '5px 8px', flexWrap: 'wrap' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: '150px' }}>
    <FileText size={11} style={{ color: '#00ba52', flexShrink: 0 }} />
    <span style={{ color: '#00ba52', fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap' }}>OC -</span>
    <input
      type="text"
      placeholder="Ej: P443 Trinidad"
      value={clientOCs[clienteId] ? clientOCs[clienteId].replace(/^OC - /, '') : ''}
      onChange={(e) => {
        // ✅ Limitar a 30 caracteres totales (incluyendo "OC - ")
        const valorLimpio = e.target.value.slice(0, 25); // 30 - 5 de "OC - "
        setClientOCs(prev => ({ ...prev, [clienteId]: 'OC - ' + valorLimpio }));
      }}
      maxLength={25}  // ✅ Límite máximo en el input (25 + "OC - " = 30)
      style={{ 
        background: 'transparent', 
        border: 'none', 
        color: '#fff', 
        fontSize: '0.65rem', 
        outline: 'none', 
        width: '100%', 
        fontFamily: 'Montserrat, sans-serif' 
      }}
    />
  </div>
  
  {/* Contador de caracteres */}
  <span style={{ 
    fontSize: '0.5rem', 
    color: (clientOCs[clienteId]?.length || 0) >= 25 ? '#f87171' : '#666',
    fontWeight: 600,
    flexShrink: 0
  }}>
    {(clientOCs[clienteId]?.replace(/^OC - /, '').length || 0)}/25
  </span>
</div>

              {/* ADJUNTAR ARCHIVO DE OC CON COMPRESIÓN */}
              <div style={{ marginTop: '6px' }}>
                <input
                  id="oc-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleOCFileChange}
                  style={{ display: 'none' }}
                />
                
                {isCompressing ? (
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: 'rgba(167,139,250,0.1)',
                    border: '1px dashed rgba(167,139,250,0.4)',
                    borderRadius: '6px',
                    padding: '8px',
                    color: '#a78bfa',
                    fontSize: '0.6rem',
                    fontWeight: 600
                  }}>
                    <Loader size={12} className="spin-anim" />
                    Comprimiendo archivo...
                  </div>
                ) : !ocFile ? (
                  <button
                    onClick={() => document.getElementById('oc-file-input').click()}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      background: 'rgba(167,139,250,0.1)',
                      border: '1px dashed rgba(167,139,250,0.4)',
                      borderRadius: '6px',
                      padding: '8px',
                      cursor: 'pointer',
                      color: '#a78bfa',
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      fontFamily: 'Montserrat, sans-serif',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(167,139,250,0.2)';
                      e.target.style.borderColor = 'rgba(167,139,250,0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(167,139,250,0.1)';
                      e.target.style.borderColor = 'rgba(167,139,250,0.4)';
                    }}
                  >
                    <FileUp size={12} />
                    Adjuntar OC (máx 1MB)
                  </button>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(167,139,250,0.15)',
                    border: '1px solid rgba(167,139,250,0.4)',
                    borderRadius: '6px',
                    padding: '6px 8px'
                  }}>
                    <Paperclip size={12} style={{ color: '#a78bfa', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.55rem',
                        color: '#c4b5fd',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {ocFileName}
                      </div>
                      <div style={{ fontSize: '0.45rem', color: '#8b7cc9' }}>
                        {ocFileSize}
                        {ocFileOriginalSize && (
                          <span style={{ marginLeft: '4px', color: '#6b5fa0' }}>
                            (original: {ocFileOriginalSize})
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveOCFile}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#f87171',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0
                      }}
                      title="Eliminar archivo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>

              {/* Comentario */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '5px 8px' }}>
                <MessageSquare size={11} style={{ color: '#a78bfa', flexShrink: 0, marginTop: '2px' }} />
                <textarea
                  placeholder="Comentario / Observación para el admin..."
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  rows={2}
                  style={{ 
                    background: 'transparent', border: 'none', color: '#fff', 
                    fontSize: '0.65rem', outline: 'none', width: '100%',
                    resize: 'none', fontFamily: 'Montserrat, sans-serif',
                    placeholderColor: '#666'
                  }}
                />
              </div>
            </div>
          </div>

          {/* BOTONES FIJOS ABAJO */}
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.8rem', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button className="save-order-btn" onClick={limpiarFormulario}
              style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', flex: 1 }}>
              <RotateCcw size={16} />
            </button>
            <button 
              className="save-order-btn" 
              onClick={() => handleSaveOrder(ocFile, ocFileName)}
              disabled={orderLines.length === 0 || !clienteId || savingOrder || !clientOCs[clienteId] || clientOCs[clienteId].replace(/^OC - /, '').trim() === ''}
              style={{ flex: 2 }}>
              {savingOrder ? <Loader2 size={16} className="spin-anim" /> : <Save size={16} />}
              <span>{savingOrder ? 'Creando...' : 'Crear Pedido'}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VistaResumen;